import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { useTranslation } from "react-i18next";
import ajaxClient, { MaxServerError } from "../../../../js/common/AjaxClient";
import { getInitialModalState, handleEditQuery, isValidEmail, transformApiResponse } from "./searchWatchdogHelpers";

// API endpoints constants
const API_ENDPOINTS = {
    GET_SEARCH_RESULTS: "/EshopWatchdog/jsGetSearchResults",
    GET_USER_SUBSCRIPTIONS: "/EshopWatchdog/jsGetUserSubscriptions",
    TOGGLE_SEARCH_WATCHDOG: "/EshopWatchdog/jsToggleSearchWatchdog",
} as const;

// Business logic constants
const BUSINESS_CONSTANTS = {
    MAX_WATCHDOGS_PER_EMAIL: 5,
    MAX_RESULT_COUNT: 200,
} as const;

interface SearchWatchdogModalProperties {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly searchQuery: string;
}

interface SearchWatchdogResponse {
    query: string;
    correctedQuery?: string;
    totalHits: number;
}

interface UserSubscription {
    id: number;
    query: string;
    canonicalQuery: string;
    createdDate: string;
    activeUntil: string;
}

interface UserSubscriptionsResponse {
    totalCount: number;
    maxAllowed: number;
    remainingSlots: number;
    subscriptions: UserSubscription[];
}

interface SubscriptionToggleResponse {
    status: string;
    message: string;
}

// eslint-disable-next-line complexity
export default function SearchWatchdogModal({ isOpen, onClose, searchQuery }: SearchWatchdogModalProperties): React.JSX.Element | undefined {
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [searchResults, setSearchResults] = useState<SearchWatchdogResponse | undefined>(undefined);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [subscriptionSuccess, setSubscriptionSuccess] = useState<boolean>(false);
    const [subscriptionResponse, setSubscriptionResponse] = useState<SubscriptionToggleResponse | undefined>(undefined);
    const [email, setEmail] = useState<string>("");
    const [userSubscriptions, setUserSubscriptions] = useState<UserSubscriptionsResponse | undefined>(undefined);
    const [loadingSubscriptions, setLoadingSubscriptions] = useState<boolean>(false);
    const [showSubscriptionsList, setShowSubscriptionsList] = useState<boolean>(false);
    const { t } = useTranslation();

    // Funkce pro načtení přehledu uživatelských hlídání
    const loadUserSubscriptions = async (emailAddress: string): Promise<void> => {
        if (!isValidEmail(emailAddress)) {
            return;
        }

        setLoadingSubscriptions(true);

        try {
            const response = await ajaxClient.get<UserSubscriptionsResponse>(
                `${API_ENDPOINTS.GET_USER_SUBSCRIPTIONS}?email=${encodeURIComponent(emailAddress.trim())}`,
            );
            setUserSubscriptions(response);
            setShowSubscriptionsList(true);
        } catch {
            // V případě chyby nezobrazujeme error message uživateli - není to kritické
            setUserSubscriptions(undefined);
            setShowSubscriptionsList(false);
        } finally {
            setLoadingSubscriptions(false);
        }
    };

    const handleSubscriptionSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();

        if (submitting || !isValidEmail(email)) {
            return;
        }

        setSubmitting(true);
        setErrorMessage(undefined);

        // API call pro toggle search watchdog subscription
        ajaxClient.post<SubscriptionToggleResponse>(API_ENDPOINTS.TOGGLE_SEARCH_WATCHDOG, {
            email: email.trim(),
            searchQuery: searchQuery,
        })
            .then((response: SubscriptionToggleResponse) => {
                setSubscriptionResponse(response);
                setSubscriptionSuccess(true);
            })
            .catch((error: unknown) => {
                if (error instanceof MaxServerError) {
                    setErrorMessage(error.message);

                    // Pokud je chyba kvůli překročení limitu, načti přehled hlídání
                    if (error.message.includes("maximální počet") || error.message.includes("maximálny počet")) {
                        void loadUserSubscriptions(email.trim());
                    }
                } else {
                    setErrorMessage(t("searchWatchdog.subscriptionError"));
                }
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    useEffect(() => {
        if (isOpen && searchQuery.trim()) {
            const initialState = getInitialModalState();
            setErrorMessage(initialState.errorMessage);
            setSearchResults(initialState.searchResults);
            setSubmitting(initialState.submitting);
            setSubscriptionSuccess(initialState.subscriptionSuccess);
            setSubscriptionResponse(initialState.subscriptionResponse);
            setEmail(initialState.email);
            setUserSubscriptions(initialState.userSubscriptions);
            setShowSubscriptionsList(initialState.showSubscriptionsList);
            setLoadingSubscriptions(initialState.loadingSubscriptions);

            // Nastavit loading na true při začátku načítání
            setLoading(true);

            ajaxClient.get<Record<string, unknown>>(`${API_ENDPOINTS.GET_SEARCH_RESULTS}?q=${encodeURIComponent(searchQuery)}`)
                .then((response: Record<string, unknown>) => {
                    const transformedResponse = transformApiResponse(response);
                    setSearchResults(transformedResponse);
                })
                .catch((error: unknown) => {
                    if (error instanceof MaxServerError) {
                        setErrorMessage(error.message);
                    } else {
                        setErrorMessage(t("searchWatchdog.searchError"));
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [isOpen, searchQuery, t]);

    if (!isOpen) {
        return undefined;
    }

    return (
        <Modal
            id="search-watchdog-modal"
            isOpen={isOpen}
            onRequestClose={onClose}
            className="modal-dialog"
            overlayClassName="react-modal-backdrop modal-transition"
            closeTimeoutMS={250}
            ariaHideApp={false}
        >
            <div className="modal-content">
                <div className="modal-header empty">
                    <button type="button" className="close" onClick={onClose}>
                        <i className="fa-solid fa-times"></i>
                    </button>

                </div>

                <div className="modal-body sw-modal-body">
                    <div className="watchdog-set">
                        <div className="well well-transparent">
                            <div className="row">
                                <p className="fakeh3">{t("searchWatchdog.modalTitle")}</p>

                                {subscriptionSuccess && subscriptionResponse && (
                                    <>
                                        <p>
                                            <span className={subscriptionResponse.status === "UNSUBSCRIBED" ? "sw-text-blue" : "sw-text-green"}><i className={`fa-solid ${subscriptionResponse.status === "UNSUBSCRIBED" ? "fa-info-circle" : "fa-check-circle"}`}></i> <strong>{subscriptionResponse.status === "UNSUBSCRIBED" ? t("searchWatchdog.watchingStopped") : t("searchWatchdog.watchingStarted")}</strong></span>
                                        </p>
                                        <p dangerouslySetInnerHTML={{ __html: subscriptionResponse.message }} />
                                        <div className="sw-modal-buttons">
                                            <button type="button" className="btn btn-secondary btn-md sw-btn-close" onClick={onClose}>{t("searchWatchdog.closeButton")}</button>
                                        </div>
                                    </>
                                )}

                                {!subscriptionSuccess && (
                                    <>
                                        {loading && (
                                            <p className="search-stats-loading">
                                                <i className="fa-solid fa-spinner fa-spin"></i> {t("searchWatchdog.loadingText")}
                                            </p>
                                        )}

                                        {(errorMessage !== undefined && errorMessage !== "") && !loading && (
                                            <>
                                                <p>
                                                    <span className="sw-text-red"><i className="fa-solid fa-exclamation-circle"></i> <strong>{t("searchWatchdog.errorOccurred")}</strong> </span> {errorMessage}
                                                </p>

                                                {/* Přehled aktivních hlídání při překročení limitu */}
                                                {showSubscriptionsList && (
                                                    <p className="user-subscriptions-overview">
                                                        <span>{t("searchWatchdog.yourActiveWatching")} ({(userSubscriptions?.totalCount ?? 0)}/{(userSubscriptions?.maxAllowed ?? BUSINESS_CONSTANTS.MAX_WATCHDOGS_PER_EMAIL)}):</span>
                                                        {loadingSubscriptions
                                                            ? (
                                                                <p><i className="fa-solid fa-spinner fa-spin"></i> {t("searchWatchdog.loadingSubscriptions")}</p>
                                                            )
                                                            : (
                                                                userSubscriptions && userSubscriptions.subscriptions.length > 0
                                                                    ? (
                                                                        <ul className="subscriptions-list">
                                                                            {userSubscriptions.subscriptions.map((sub) => (
                                                                                <li key={sub.id} className="subscription-item">
                                                                                    <span className="search-watchdog-phrase">{sub.query}</span>
                                                                                    <small> ({t("searchWatchdog.activeUntil")} {sub.activeUntil})</small>
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    )
                                                                    : (
                                                                        <p>{t("searchWatchdog.noActiveWatching")}</p>
                                                                    )
                                                            )}
                                                    </p>
                                                )}

                                                <div className="sw-modal-buttons">
                                                    <button type="button" className="btn btn-secondary btn-md sw-btn-close" onClick={onClose}>{t("searchWatchdog.closeButton")}</button>
                                                </div>
                                            </>
                                        )}

                                        {searchResults && !loading && (
                                            <>
                                                {(searchResults.correctedQuery !== undefined && searchResults.correctedQuery !== "") && searchResults.totalHits < BUSINESS_CONSTANTS.MAX_RESULT_COUNT && (
                                                    <div>
                                                        <p>
                                                            {t("searchWatchdog.correctedQuery").split("{{originalQuery}}")[0]}
                                                            <span className="search-watchdog-phrase">{searchQuery}</span>
                                                            {t("searchWatchdog.correctedQuery").split("{{originalQuery}}")[1]?.split("{{correctedQuery}}")[0]}
                                                            <span className="search-watchdog-phrase">{searchResults.query}</span>
                                                            {t("searchWatchdog.correctedQuery").split("{{correctedQuery}}")[1]?.split("{{count}}")[0]}
                                                            {searchResults.totalHits}
                                                            {t("searchWatchdog.correctedQuery").split("{{count}}")[1]}
                                                        </p>
                                                    </div>
                                                )}

                                                {searchResults.totalHits > BUSINESS_CONSTANTS.MAX_RESULT_COUNT && (searchResults.correctedQuery === undefined || searchResults.correctedQuery === "") && (
                                                    <div>
                                                        <p>
                                                            {t("searchWatchdog.tooGenericQuery").split("{{query}}")[0]}
                                                            <span className="search-watchdog-phrase">{searchQuery}</span>
                                                            {t("searchWatchdog.tooGenericQuery").split("{{query}}")[1]}
                                                        </p>
                                                        <p>
                                                            {t("searchWatchdog.tooManyResults", {
                                                                maxResults: BUSINESS_CONSTANTS.MAX_RESULT_COUNT,
                                                                count: searchResults.totalHits,
                                                            })}
                                                        </p>
                                                        <p className="sw-modal-title">
                                                            <span className="sw-modal-title-em">{t("searchWatchdog.tipsTitle")}</span>
                                                        </p>
                                                        <p>
                                                            <ul className="sw-modal-list">
                                                                <li dangerouslySetInnerHTML={{ __html: t("searchWatchdog.tipBeSpecific") }} />
                                                                <li dangerouslySetInnerHTML={{ __html: t("searchWatchdog.tipUseProductName") }} />
                                                                <li dangerouslySetInnerHTML={{ __html: t("searchWatchdog.tipAddDetails") }} />
                                                            </ul>
                                                        </p>
                                                        <div className="sw-modal-buttons">
                                                            <button type="button" className="btn btn-success btn-md" onClick={() => handleEditQuery(onClose)}>
                                                                <i className="fas fa-edit"></i> {t("searchWatchdog.editQueryButton")}
                                                            </button>
                                                            <button type="button" className="btn btn-secondary btn-md" onClick={onClose}>
                                                                {t("searchWatchdog.closeButton")}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {(searchResults.totalHits < BUSINESS_CONSTANTS.MAX_RESULT_COUNT || (searchResults.correctedQuery !== undefined && searchResults.correctedQuery !== "")) && (errorMessage === undefined || errorMessage === "") && (
                                                    <>
                                                        <p>
                                                            {t("searchWatchdog.subscribeInstructions").split("{{query}}")[0]}
                                                            <span className="search-watchdog-phrase">{searchQuery}</span>
                                                            {t("searchWatchdog.subscribeInstructions").split("{{query}}")[1]}
                                                        </p>
                                                        <div className="watchdog-container">
                                                            <div className="watchdog-inner-form">
                                                                <form onSubmit={handleSubscriptionSubmit}>
                                                                    <div className="form-group">
                                                                        <div className="input-form-mandatory" aria-required="true">
                                                                            <input
                                                                                className="form-control"
                                                                                placeholder={t("searchWatchdog.emailPlaceholder")}
                                                                                id="categoryWatchdog_email"
                                                                                name="email"
                                                                                type="email"
                                                                                value={email}
                                                                                onChange={(event) => setEmail(event.target.value)}
                                                                                disabled={submitting}
                                                                                required
                                                                            />
                                                                            <label htmlFor="categoryWatchdog_email" className="error"></label>
                                                                        </div>
                                                                    </div>
                                                                    <button
                                                                        type="submit"
                                                                        className="btn btn-success btn-sm"
                                                                        disabled={submitting || !isValidEmail(email)}
                                                                    >
                                                                        {submitting
                                                                            ? (
                                                                                <p>
                                                                                    <i className="fa-solid fa-spinner fa-spin"></i> {t("searchWatchdog.submitButtonLoading")}
                                                                                </p>
                                                                            )
                                                                            : (
                                                                                t("searchWatchdog.submitButton")
                                                                            )}
                                                                    </button>
                                                                </form>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
